import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import BP_GO_TERMS from './bpGoTerms';

// Minimum horizontal space per bar slot (incl. padding). With 20 BP terms we
// generally exceed the available container width, so the chart is rendered at
// its intrinsic width inside a horizontally scrollable wrapper.
const MIN_SLOT_WIDTH = 70;

// Bar chart of BP GO terms (x) vs. number of proteins associated (y).
// Clicking a bar toggles that term in `selectedGoIds`.
export default function BpGoTermBarChart({
  rowData,
  selectedGoIds,
  onToggle,
  isDark,
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const counts = useMemo(() => {
    const map = new Map(BP_GO_TERMS.map(t => [t.id, 0]));
    if (!Array.isArray(rowData)) return map;
    for (const row of rowData) {
      const bpTerms = Array.isArray(row.gotermBpAll) ? row.gotermBpAll : [];
      const seen = new Set();
      for (const t of bpTerms) {
        const id = typeof t === 'string' ? t : (t?.go_id || t?.id || '');
        if (id && map.has(id) && !seen.has(id)) {
          map.set(id, map.get(id) + 1);
          seen.add(id);
        }
      }
    }
    return map;
  }, [rowData]);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => setContainerWidth(el.clientWidth || 0);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Left margin must accommodate the rotated x-axis label of the FIRST bar,
  // which extends up-and-left past the y-axis. Right margin is bumped by the
  // same amount so the plotting area stays visually centered.
  const margin = { top: 26, right: 46, bottom: 200, left: 55 };
  const intrinsicWidth = Math.max(
    containerWidth,
    BP_GO_TERMS.length * MIN_SLOT_WIDTH + margin.left + margin.right,
  );

  useEffect(() => {
    if (!svgRef.current || intrinsicWidth <= 0) return;

    const data = BP_GO_TERMS.map(t => ({
      id: t.id,
      label: t.label,
      count: counts.get(t.id) || 0,
    }));

    const W = intrinsicWidth;
    const H = 360;
    const innerW = Math.max(0, W - margin.left - margin.right);
    const innerH = Math.max(0, H - margin.top - margin.bottom);

    const x = d3.scaleBand()
      .domain(data.map(d => d.id))
      .range([0, innerW])
      .padding(0.2);

    const yMax = d3.max(data, d => d.count) || 1;
    const y = d3.scaleLinear()
      .domain([0, yMax])
      .nice()
      .range([innerH, 0]);

    const fg = isDark ? '#ddd' : '#222';
    const grid = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
    const barFill = isDark ? '#5fa8ff' : '#2a6fc7';
    const barFillSelected = d3.interpolateRgb(barFill, '#fff')(isDark ? 0.45 : 0.55);
    const barFillHover = isDark ? '#7fbcff' : '#1f5aa8';

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('viewBox', `0 0 ${W} ${H}`)
       .attr('width', W)
       .attr('height', H);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const yAxis = d3.axisLeft(y).ticks(5).tickSizeOuter(0);
    const yAxisG = g.append('g').call(yAxis);
    yAxisG.selectAll('text').attr('fill', fg).attr('font-size', 11);
    yAxisG.selectAll('line').attr('stroke', fg).attr('stroke-opacity', 0.4);
    yAxisG.selectAll('path').attr('stroke', fg).attr('stroke-opacity', 0.4);

    g.append('g')
      .attr('class', 'bp-grid')
      .selectAll('line')
      .data(y.ticks(5))
      .join('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', d => y(d))
      .attr('y2', d => y(d))
      .attr('stroke', grid);

    g.append('text')
      .attr('transform', `translate(${-margin.left + 14},${innerH / 2}) rotate(-90)`)
      .attr('text-anchor', 'middle')
      .attr('fill', fg)
      .attr('font-size', 11)
      .text('# proteins');

    // Non-zero bars get a minimum height so low-count terms stay visible.
    const MIN_BAR_H = 3;
    const barHeight = d => (d.count <= 0 ? 0 : Math.max(MIN_BAR_H, innerH - y(d.count)));
    const barY = d => innerH - barHeight(d);

    // Bars (visual only). Interaction is handled by the full-height hit areas
    // below, so clicking does not depend on how tall the bar is.
    const bars = g.append('g').selectAll('rect.bp-bar')
      .data(data)
      .join('rect')
      .attr('class', 'bp-bar')
      .attr('x', d => x(d.id))
      .attr('y', barY)
      .attr('width', x.bandwidth())
      .attr('height', barHeight)
      .attr('fill', d => selectedGoIds.has(d.id) ? barFillSelected : barFill)
      .attr('stroke', d => selectedGoIds.has(d.id) ? fg : 'none')
      .attr('stroke-width', d => selectedGoIds.has(d.id) ? 1.5 : 0)
      .style('pointer-events', 'none');

    // Full-height transparent hit areas: the entire column is clickable, so a
    // low-count bar is as easy to click as a tall one.
    const hit = g.append('g').selectAll('rect.bp-hit')
      .data(data)
      .join('rect')
      .attr('class', 'bp-hit')
      .attr('x', d => x(d.id))
      .attr('y', 0)
      .attr('width', x.bandwidth())
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer')
      .on('mouseover', (_event, d) => {
        bars.filter(b => b.id === d.id)
          .attr('fill', selectedGoIds.has(d.id) ? barFillSelected : barFillHover);
      })
      .on('mouseout', (_event, d) => {
        bars.filter(b => b.id === d.id)
          .attr('fill', selectedGoIds.has(d.id) ? barFillSelected : barFill);
      })
      .on('click', (_event, d) => {
        onToggle(d.id, !selectedGoIds.has(d.id));
      });

    hit.append('title').text(d => `${d.label} (${d.id})\n${d.count} proteins\nClick to ${selectedGoIds.has(d.id) ? 'remove' : 'apply'} filter`);

    g.append('g').selectAll('text.bp-count')
      .data(data)
      .join('text')
      .attr('class', 'bp-count')
      .attr('x', d => x(d.id) + x.bandwidth() / 2)
      .attr('y', d => barY(d) - 6)
      .attr('text-anchor', 'middle')
      .attr('fill', fg)
      .attr('font-size', 11)
      .attr('font-weight', 600)
      .style('pointer-events', 'none')
      .text(d => d.count);

    const xAxisG = g.append('g')
      .attr('transform', `translate(0,${innerH})`);

    xAxisG.append('line')
      .attr('x1', 0).attr('x2', innerW)
      .attr('stroke', fg).attr('stroke-opacity', 0.4);

    const tickG = xAxisG.selectAll('g.bp-tick')
      .data(data)
      .join('g')
      .attr('class', 'bp-tick')
      .attr('transform', d => `translate(${x(d.id) + x.bandwidth() / 2},6)`)
      .style('cursor', 'pointer')
      .on('click', (_event, d) => {
        onToggle(d.id, !selectedGoIds.has(d.id));
      });

    tickG.append('text')
      .attr('transform', 'rotate(-40)')
      .attr('text-anchor', 'end')
      .attr('dy', '0.35em')
      .attr('fill', fg)
      .attr('font-size', 11)
      .attr('font-weight', d => selectedGoIds.has(d.id) ? 700 : 400)
      .text(d => d.label);

    tickG.append('text')
      .attr('transform', 'rotate(-40) translate(0, 13)')
      .attr('text-anchor', 'end')
      .attr('dy', '0.35em')
      .attr('fill', fg)
      .attr('fill-opacity', 0.6)
      .attr('font-size', 9)
      .text(d => d.id);
  }, [counts, intrinsicWidth, isDark, selectedGoIds, onToggle]);

  return (
    <div ref={containerRef} style={{ width: '100%', overflowX: 'auto' }}>
      <svg ref={svgRef} style={{ display: 'block' }} />
    </div>
  );
}
